import { useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useParams } from "react-router-dom";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { format } from "date-fns";
import {
  AlertCircle,
  BookPlus,
  Check,
  CheckCircle2,
  Loader2,
  MessageSquare,
  Pencil,
  Sparkles,
  X,
  XCircle,
} from "lucide-react";
import {
  fetchChatStats,
  fetchChatConversations,
  fetchChatSession,
  fetchChatHealth,
  patchConversation,
  createKnowledgeChunk,
  draftKbEntry,
  type ChatMessage,
  type KbDraft,
} from "./chatApi";
import { KnowledgeBaseView } from "./views/KnowledgeBaseView";

const CHAT_QUERY_CLIENT = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function shortDate(value: string | null): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return format(d, "MMM d, yyyy h:mm a");
}

function ChatHeader() {
  const tabs = [
    { to: "", label: "Dashboard" },
    { to: "conversations", label: "Conversations" },
    { to: "kb", label: "Knowledge Base" },
    { to: "health", label: "n8n Health" },
    { to: "embed", label: "Embed Snippet" },
  ] as const;

  return (
    <header className="flex items-center justify-between border-b border-tribal-border bg-tribal-surface px-4 py-3">
      <div className="flex min-w-0 items-center gap-4">
        <h1 className="text-lg font-semibold tracking-tight">
          <span className="text-tribal-red">Tribal Axe</span> Chatbot
        </h1>
        <nav
          aria-label="Chat views"
          className="hidden items-center gap-0.5 rounded-lg bg-black/30 p-0.5 sm:flex"
        >
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.to === ""}
              className={({ isActive }) =>
                `rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-tribal-border text-white"
                    : "text-tribal-muted hover:text-white"
                }`
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
}

function QueryError({ message }: { message: string }) {
  return (
    <div className="m-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
      <div className="flex items-center gap-2">
        <AlertCircle className="h-4 w-4" />
        {message}
      </div>
    </div>
  );
}

function DashboardView() {
  const stats = useQuery({ queryKey: ["chat", "stats"], queryFn: fetchChatStats });
  const recent = useQuery({
    queryKey: ["chat", "conversations", "recent"],
    queryFn: () => fetchChatConversations({ limit: 10 }),
  });

  if (stats.isLoading || recent.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-tribal-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading dashboard...
      </div>
    );
  }
  if (stats.isError) {
    return <QueryError message={`Failed to load stats: ${(stats.error as Error).message}`} />;
  }

  const s = stats.data!;
  const hasUnanswered = s.unansweredMessages > 0;

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <div className="text-xs uppercase tracking-wide text-tribal-muted">Total Messages</div>
          <div className="mt-1 text-2xl font-semibold text-white">{s.totalMessages}</div>
        </div>
        <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <div className="text-xs uppercase tracking-wide text-tribal-muted">Today</div>
          <div className="mt-1 text-2xl font-semibold text-white">{s.todayMessages}</div>
        </div>
        <div
          className={`rounded-lg border p-4 ${
            hasUnanswered
              ? "border-amber-500/40 bg-amber-500/10"
              : "border-tribal-border bg-tribal-surface"
          }`}
        >
          <div className="text-xs uppercase tracking-wide text-tribal-muted">Unanswered</div>
          <div className={`mt-1 text-2xl font-semibold ${hasUnanswered ? "text-amber-400" : "text-white"}`}>
            {s.unansweredMessages}
          </div>
          {hasUnanswered && (
            <Link
              to="/chat/conversations"
              onClick={() => {/* filter will default; user can click Unanswered */}}
              className="mt-1 inline-block text-xs font-medium text-amber-400 hover:text-amber-300"
            >
              Review now &rarr;
            </Link>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-tribal-border bg-tribal-surface">
        <div className="border-b border-tribal-border px-4 py-3 text-sm font-semibold text-white">
          Recent Messages
        </div>
        <div className="divide-y divide-tribal-border">
          {recent.data?.rows.length === 0 && (
            <p className="py-8 text-center text-sm text-tribal-muted">No messages yet</p>
          )}
          {recent.data?.rows.map((msg) => (
            <MessageRow key={msg.id} msg={msg} />
          ))}
        </div>
      </div>
    </div>
  );
}

function AnsweredBadge({ msg }: { msg: ChatMessage }) {
  const answered = msg.was_answered ?? !!msg.bot_response;
  return answered ? (
    <span className="inline-block rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white">
      answered
    </span>
  ) : (
    <span className="inline-block rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white">
      unanswered
    </span>
  );
}

function MessageRow({ msg }: { msg: ChatMessage }) {
  return (
    <Link
      to={msg.session_id ? `/chat/conversations/${msg.session_id}` : `/chat/conversations`}
      className="block px-4 py-3 transition-colors hover:bg-white/5"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-3.5 w-3.5 shrink-0 text-tribal-muted" />
            <p className="truncate text-sm font-medium text-white">{msg.user_message}</p>
          </div>
          {msg.bot_response && (
            <p className="mt-1 truncate pl-5.5 text-xs text-tribal-muted">
              {msg.bot_response}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <AnsweredBadge msg={msg} />
          <span className="text-xs text-tribal-muted">{shortDate(msg.created_at)}</span>
        </div>
      </div>
    </Link>
  );
}

function ConversationsView() {
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<string>("all");
  const limit = 25;

  const q = useQuery({
    queryKey: ["chat", "conversations", filter, page],
    queryFn: () =>
      fetchChatConversations({
        limit,
        offset: page * limit,
        answered: filter === "all" ? undefined : filter,
      }),
  });

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-tribal-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading conversations...
      </div>
    );
  }
  if (q.isError) {
    return <QueryError message={`Failed to load: ${(q.error as Error).message}`} />;
  }

  const { rows, total } = q.data!;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex items-center gap-2">
        {["all", "true", "false"].map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => { setFilter(v); setPage(0); }}
            className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
              filter === v
                ? "bg-tribal-border text-white"
                : "text-tribal-muted hover:text-white"
            }`}
          >
            {v === "all" ? "All" : v === "true" ? "Answered" : "Unanswered"}
          </button>
        ))}
        <span className="ml-auto text-xs text-tribal-muted">
          {total} message{total !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="rounded-lg border border-tribal-border bg-tribal-surface">
        <div className="divide-y divide-tribal-border">
          {rows.length === 0 && (
            <p className="py-8 text-center text-sm text-tribal-muted">No messages found</p>
          )}
          {rows.map((msg) => (
            <MessageRow key={msg.id} msg={msg} />
          ))}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-md border border-tribal-border px-3 py-1.5 text-sm text-tribal-muted transition-colors hover:bg-tribal-border hover:text-white disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-xs text-tribal-muted">
            Page {page + 1} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-md border border-tribal-border px-3 py-1.5 text-sm text-tribal-muted transition-colors hover:bg-tribal-border hover:text-white disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function AddToKbModal({
  question,
  onClose,
}: {
  question: string;
  onClose: () => void;
}) {
  const [step, setStep] = useState<"input" | "review">("input");
  const [answer, setAnswer] = useState("");
  const [editableQuestion, setEditableQuestion] = useState(question);
  const [draft, setDraft] = useState<KbDraft | null>(null);

  // Editable fields for the review step
  const [editQuestions, setEditQuestions] = useState<string[]>([]);
  const [editAnswer, setEditAnswer] = useState("");
  const [editCategory, setEditCategory] = useState("");

  const queryClient = useQueryClient();

  const draftMut = useMutation({
    mutationFn: ({ q, a }: { q: string; a: string }) => draftKbEntry(q, a),
    onSuccess: (data) => {
      setDraft(data);
      setEditQuestions(data.questions);
      setEditAnswer(data.answer);
      setEditCategory(data.category);
      setStep("review");
    },
  });

  const saveMut = useMutation({
    mutationFn: (content: string) => createKnowledgeChunk(content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat", "kb"] });
      onClose();
    },
  });

  const handleDraft = () => {
    draftMut.mutate({ q: editableQuestion.trim(), a: answer.trim() });
  };

  const buildContent = () => {
    const qLines = editQuestions
      .filter((q) => q.trim())
      .map((q) => `Q: ${q}`)
      .join("\n");
    return `${qLines}\nA: ${editAnswer}`;
  };

  const handleSave = () => {
    saveMut.mutate(buildContent());
  };

  const updateQuestion = (idx: number, val: string) => {
    setEditQuestions((prev) => prev.map((q, i) => (i === idx ? val : q)));
  };

  const removeQuestion = (idx: number) => {
    setEditQuestions((prev) => prev.filter((_, i) => i !== idx));
  };

  const addQuestion = () => {
    setEditQuestions((prev) => [...prev, ""]);
  };

  const isPending = draftMut.isPending || saveMut.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative mx-4 w-full max-w-2xl rounded-xl border border-tribal-border bg-tribal-bg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-tribal-border px-5 py-4">
          <div className="flex items-center gap-2">
            <BookPlus className="h-4 w-4 text-indigo-400" />
            <h3 className="text-sm font-semibold text-white">
              {step === "input" ? "Add to Knowledge Base" : "Review AI Draft"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="rounded-md p-1 text-tribal-muted transition-colors hover:bg-tribal-border hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {step === "input" && (
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-tribal-muted">
                  Customer Question
                </label>
                <textarea
                  className="w-full resize-y rounded-md border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white placeholder:text-tribal-muted focus:border-indigo-400 focus:outline-none"
                  rows={2}
                  value={editableQuestion}
                  onChange={(e) => setEditableQuestion(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-tribal-muted">
                  Your Answer
                </label>
                <textarea
                  className="w-full resize-y rounded-md border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white placeholder:text-tribal-muted focus:border-indigo-400 focus:outline-none"
                  rows={4}
                  placeholder="Write the correct answer — AI will polish it for the KB..."
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  autoFocus
                />
              </div>
              {draftMut.isError && (
                <p className="text-xs text-red-300">
                  Draft failed: {(draftMut.error as Error).message}
                </p>
              )}
            </div>
          )}

          {step === "review" && draft && (
            <div className="space-y-4">
              {/* Category badge */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-tribal-muted">
                  Category
                </label>
                <input
                  type="text"
                  className="rounded-md border border-tribal-border bg-black/30 px-3 py-1.5 text-sm text-indigo-300 focus:border-indigo-400 focus:outline-none"
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                />
              </div>

              {/* Question variations */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-medium text-tribal-muted">
                    Question Variations
                  </label>
                  <button
                    type="button"
                    onClick={addQuestion}
                    className="text-xs text-indigo-400 transition-colors hover:text-indigo-300"
                  >
                    + Add variation
                  </button>
                </div>
                <div className="space-y-2">
                  {editQuestions.map((q, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="mt-2 text-xs text-tribal-muted">Q{i + 1}</span>
                      <input
                        type="text"
                        className="flex-1 rounded-md border border-tribal-border bg-black/30 px-3 py-1.5 text-sm text-white focus:border-indigo-400 focus:outline-none"
                        value={q}
                        onChange={(e) => updateQuestion(i, e.target.value)}
                      />
                      {editQuestions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeQuestion(i)}
                          className="mt-1 rounded-md p-1 text-tribal-muted transition-colors hover:text-red-400"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Polished answer */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-tribal-muted">
                  Polished Answer
                </label>
                <textarea
                  className="w-full resize-y rounded-md border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white focus:border-indigo-400 focus:outline-none"
                  rows={5}
                  value={editAnswer}
                  onChange={(e) => setEditAnswer(e.target.value)}
                />
              </div>

              {/* Preview */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-tribal-muted">
                  Final KB Entry Preview
                </label>
                <pre className="whitespace-pre-wrap rounded-md border border-tribal-border bg-black/40 px-3 py-2 text-xs text-tribal-muted">
                  {buildContent()}
                </pre>
              </div>

              {saveMut.isError && (
                <p className="text-xs text-red-300">
                  Save failed: {(saveMut.error as Error).message}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-tribal-border px-5 py-3">
          {step === "input" && (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={isPending}
                className="rounded-md px-3 py-1.5 text-xs text-tribal-muted transition-colors hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!editableQuestion.trim() || !answer.trim() || isPending}
                onClick={handleDraft}
                className="flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white transition-colors hover:bg-indigo-500 disabled:opacity-40"
              >
                {draftMut.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                Draft with AI
              </button>
            </>
          )}

          {step === "review" && (
            <>
              <button
                type="button"
                disabled={isPending}
                onClick={() => setStep("input")}
                className="flex items-center gap-1 rounded-md px-3 py-1.5 text-xs text-tribal-muted transition-colors hover:text-white"
              >
                <Pencil className="h-3 w-3" /> Back to Edit
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={isPending}
                  onClick={handleDraft}
                  className="flex items-center gap-1 rounded-md border border-indigo-500/40 px-3 py-1.5 text-xs text-indigo-300 transition-colors hover:bg-indigo-500/20 disabled:opacity-40"
                >
                  {draftMut.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  Re-draft
                </button>
                <button
                  type="button"
                  disabled={
                    !editQuestions.some((q) => q.trim()) ||
                    !editAnswer.trim() ||
                    isPending
                  }
                  onClick={handleSave}
                  className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-1.5 text-xs font-medium text-white transition-colors hover:bg-emerald-500 disabled:opacity-40"
                >
                  {saveMut.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Check className="h-3 w-3" />
                  )}
                  Approve &amp; Save to KB
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SessionDetailView() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const queryClient = useQueryClient();
  const [addToKbId, setAddToKbId] = useState<number | null>(null);

  const q = useQuery({
    queryKey: ["chat", "session", sessionId],
    queryFn: () => fetchChatSession(sessionId!),
    enabled: !!sessionId,
  });

  const markAnswered = useMutation({
    mutationFn: ({ id, val }: { id: number; val: boolean }) =>
      patchConversation(id, { was_answered: val }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat", "session", sessionId] });
      queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
      queryClient.invalidateQueries({ queryKey: ["chat", "stats"] });
    },
  });

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-tribal-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading session...
      </div>
    );
  }
  if (q.isError) {
    return <QueryError message={`Failed to load session: ${(q.error as Error).message}`} />;
  }

  const messages = q.data ?? [];

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex items-center gap-3">
        <Link
          to="/chat/conversations"
          className="rounded-md border border-tribal-border px-3 py-1.5 text-sm text-tribal-muted transition-colors hover:bg-tribal-border hover:text-white"
        >
          &larr; Back
        </Link>
        <h2 className="text-sm font-medium text-tribal-muted">
          Session <span className="font-mono text-white">{sessionId}</span>
        </h2>
        <span className="text-xs text-tribal-muted">
          {messages.length} message{messages.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="space-y-3">
        {messages.map((msg) => {
          const isUnanswered = !(msg.was_answered ?? !!msg.bot_response);
          return (
            <div key={msg.id} className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-tribal-muted">{shortDate(msg.created_at)}</span>
                <div className="flex items-center gap-2">
                  <AnsweredBadge msg={msg} />
                  {isUnanswered && (
                    <>
                      <button
                        type="button"
                        title="Mark as answered"
                        disabled={markAnswered.isPending}
                        onClick={() => markAnswered.mutate({ id: msg.id, val: true })}
                        className="flex items-center gap-1 rounded-md border border-tribal-border px-2 py-0.5 text-xs text-tribal-muted transition-colors hover:border-emerald-500/40 hover:text-emerald-400"
                      >
                        <Check className="h-3 w-3" /> Mark answered
                      </button>
                      <button
                        type="button"
                        title="Add to Knowledge Base"
                        onClick={() => setAddToKbId(addToKbId === msg.id ? null : msg.id)}
                        className="flex items-center gap-1 rounded-md border border-tribal-border px-2 py-0.5 text-xs text-tribal-muted transition-colors hover:border-indigo-500/40 hover:text-indigo-400"
                      >
                        <BookPlus className="h-3 w-3" /> Add to KB
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="mt-3 space-y-2">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-tribal-red">User</div>
                  <p className="mt-0.5 text-sm text-white">{msg.user_message}</p>
                </div>
                {msg.bot_response && (
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wide text-emerald-400">Bot</div>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-tribal-muted">{msg.bot_response}</p>
                  </div>
                )}
              </div>
              {addToKbId === msg.id && (
                <AddToKbModal
                  question={msg.user_message}
                  onClose={() => {
                    setAddToKbId(null);
                    markAnswered.mutate({ id: msg.id, val: true });
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HealthView() {
  const q = useQuery({
    queryKey: ["chat", "health"],
    queryFn: fetchChatHealth,
    refetchInterval: 60_000,
  });

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-tribal-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking n8n...
      </div>
    );
  }
  if (q.isError) {
    return <QueryError message={`Health check failed: ${(q.error as Error).message}`} />;
  }

  const h = q.data!;
  const Icon = h.status === "healthy" ? CheckCircle2 : h.status === "degraded" ? AlertCircle : XCircle;
  const color =
    h.status === "healthy"
      ? "text-emerald-400"
      : h.status === "degraded"
        ? "text-amber-400"
        : "text-red-400";
  const bg =
    h.status === "healthy"
      ? "border-emerald-500/30 bg-emerald-500/10"
      : h.status === "degraded"
        ? "border-amber-500/30 bg-amber-500/10"
        : "border-red-500/30 bg-red-500/10";

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className={`rounded-lg border ${bg} p-6`}>
        <div className="flex items-center gap-3">
          <Icon className={`h-8 w-8 ${color}`} />
          <div>
            <h2 className={`text-lg font-semibold ${color}`}>
              {h.status === "healthy" ? "n8n Workflow Healthy" : h.status === "degraded" ? "n8n Workflow Degraded" : "n8n Workflow Unreachable"}
            </h2>
            {h.httpStatus && (
              <p className="text-sm text-tribal-muted">HTTP {h.httpStatus}</p>
            )}
            {h.error && <p className="text-sm text-red-300">{h.error}</p>}
          </div>
        </div>
      </div>

      {h.detail && (
        <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <div className="text-xs uppercase tracking-wide text-tribal-muted">Detail</div>
          <code className="mt-1 block text-sm text-white">{h.detail}</code>
        </div>
      )}

      <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
        <div className="text-xs uppercase tracking-wide text-tribal-muted">Auto-refresh</div>
        <p className="mt-1 text-sm text-tribal-muted">Health is rechecked every 60 seconds</p>
      </div>
    </div>
  );
}

function EmbedView() {
  // `webhookUrl` points at the gateway proxy, never at n8n directly. The n8n URL
  // used to ship here, which put it in the public page source of every site
  // running this snippet — see P2-4. The proxy rate-limits per IP and adds the
  // shared secret server-side.
  //
  // The script host is the WordPress uploads path the live site actually serves
  // chat.js from. It previously read cdn.agentengines.ai, which does not resolve.
  // The object must be `AgentEnginesConfig`. chat.js reads exactly that name
  // (`Object.assign(defaults, window.AgentEnginesConfig || {})`); the snippet
  // used to emit `RivelloConfig`, which the widget ignores entirely — so the
  // snippet silently did nothing but look correct.
  const snippet = `<script>
  window.AgentEnginesConfig = {
    clientId: "tribal-axe",
    webhookUrl: "https://hq-gateway.agentengines.workers.dev/public/chat",
    primaryColor: "#e74c3c",
    businessName: "Tribal Axe",
    welcomeMessage: "Hey there! I'm Axe, your guide to Tribal Axe. What can I help you with?",
    placeholder: "Ask me anything..."
  };
</script>
<script src="https://tribalaxe.com/wp-content/uploads/chat.js"></script>`;

  const [copied, setCopied] = useState(false);

  function copyToClipboard() {
    navigator.clipboard
      .writeText(snippet)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">Widget Embed Code</h2>
          <button
            type="button"
            onClick={copyToClipboard}
            className="rounded-md border border-tribal-border px-3 py-1.5 text-xs font-medium text-tribal-muted transition-colors hover:bg-tribal-border hover:text-white"
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        <p className="mt-1 text-xs text-tribal-muted">
          Add this to any HTML page to enable the Tribal Axe chat widget.
        </p>
        <pre className="mt-3 overflow-x-auto rounded-md bg-black/40 p-4 text-xs text-emerald-300">
          <code>{snippet}</code>
        </pre>
      </div>

      <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
        <h2 className="text-sm font-semibold text-white">WordPress Plugin</h2>
        <p className="mt-1 text-sm text-tribal-muted">
          For WordPress sites, install the AgentEngines Chat plugin from{" "}
          <code className="text-xs text-white">wp-content/plugins/agentengines-chat/</code>{" "}
          and configure via <strong>Settings &rarr; AgentEngines Chat</strong>.
        </p>
      </div>

      <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
        <h2 className="text-sm font-semibold text-white">CDN Assets</h2>
        <div className="mt-2 space-y-1 text-sm text-tribal-muted">
          <p>Widget JS: <code className="text-xs text-white">https://cdn.agentengines.ai/widget/chat.js</code></p>
          <p>Widget CSS: <code className="text-xs text-white">https://cdn.agentengines.ai/widget/chat.css</code></p>
        </div>
      </div>
    </div>
  );
}

function ChatModuleInner() {
  return (
    <>
      <ChatHeader />
      <Routes>
        <Route index element={<DashboardView />} />
        <Route path="conversations" element={<ConversationsView />} />
        <Route path="conversations/:sessionId" element={<SessionDetailView />} />
        <Route path="kb" element={<KnowledgeBaseView />} />
        <Route path="health" element={<HealthView />} />
        <Route path="embed" element={<EmbedView />} />
        <Route path="*" element={<Navigate to="/chat" replace />} />
      </Routes>
    </>
  );
}

export function ChatModule() {
  return (
    <QueryClientProvider client={CHAT_QUERY_CLIENT}>
      <ChatModuleInner />
    </QueryClientProvider>
  );
}
